import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { Shield, Users, GraduationCap, BookOpen, LifeBuoy, FlaskConical, ArrowRight, Layers } from "lucide-react";

const DIVISIONS = [
  { id: 1, to: "/enterprise-security", icon: Shield, title: "Enterprise Cybersecurity",
    role: "Protecting regulated and high-growth enterprises",
    desc: "VAPT, endpoint protection, DLP, identity, ZTNA, managed SOC and compliance — packaged with deployment expertise.",
    pillars: ["VAPT & Audits", "Endpoint & SOC", "DLP & Identity", "Zero Trust", "Compliance & GRC"],
    accent: "from-[#3B82F6] to-[#6D28D9]" },
  { id: 2, to: "/awareness", icon: Users, title: "Cyber Awareness Initiative",
    role: "A digital safety movement for citizens",
    desc: "Workshops, campaigns and on-ground programs — seniors, schools, RWAs, merchants and corporates — feeding the 1930 reporting ecosystem.",
    pillars: ["Senior Citizen Safety", "School Programs", "Scam Awareness", "1930 Reporting", "Digital Hygiene"],
    accent: "from-cyan-500 to-blue-500" },
  { id: 3, to: "/workforce", icon: GraduationCap, title: "Cyber Workforce & Skill Development",
    role: "Cyber talent infrastructure",
    desc: "Skill India aligned training, internship pipelines, deployment engineers and SOC analysts — placed across enterprise and project deployments.",
    pillars: ["SOC Analyst Training", "Internship Pipeline", "Skill India Alignment", "Deployment Engineering", "Industry Readiness"],
    accent: "from-amber-500 to-orange-500" },
  { id: 4, to: "/training", icon: BookOpen, title: "Corporate Security Training",
    role: "Building security culture in enterprises",
    desc: "Phishing simulation, employee awareness, leadership cyber programs, compliance training and secure-workplace operations.",
    pillars: ["Phishing Simulation", "Employee Awareness", "Leadership Programs", "Compliance Training", "Secure Workplace"],
    accent: "from-emerald-500 to-teal-500" },
  { id: 5, to: "/assistance", icon: LifeBuoy, title: "Cyber Assistance & Support",
    role: "Digital safety response for everyone",
    desc: "Guidance for individuals, fraud-reporting support, enterprise consultation, and digital-safety help for community groups.",
    pillars: ["Basic Cyber Guidance", "Reporting Assistance", "Digital Safety Help", "Enterprise Consultation", "Awareness Support"],
    accent: "from-rose-500 to-pink-500" },
  { id: 6, to: "/labs", icon: FlaskConical, title: "Future SOC & Security Labs",
    role: "Long-horizon AI security research",
    desc: "AI-assisted detection, automation, deepfake research, threat intelligence and the playbooks that will run the next-decade SOC.",
    pillars: ["AI Security Vision", "Automation & Response", "Threat Intelligence", "Deepfake Research", "Future SOC Roadmap"],
    accent: "from-violet-500 to-fuchsia-500" },
];

export default function Ecosystem() {
  return (
    <div data-testid="ecosystem-page">
      <PageHero
        eyebrow="The Ecosystem"
        title="Six divisions. One integrated cybersecurity ecosystem."
        description="Cosmos Defence is built as an ecosystem — enterprise security, awareness, workforce, training, assistance and future research — operating together to protect people, businesses and digital infrastructure."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact?type=consultation" className="btn-accent" data-testid="ecosystem-hero-cta">Request Consultation <ArrowRight size={16} /></Link>
          <Link to="/about" className="btn-ghost" data-testid="ecosystem-about-cta">Why we exist</Link>
        </div>
      </PageHero>

      <section className="py-20 bg-white">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Architecture"
            title="An ecosystem, mapped"
            description="Each division is independent in operation and unified in mission. Click any division to enter its full architecture."
          />
          <div className="mt-16 space-y-6">
            {DIVISIONS.map((d, i) => (
              <Link to={d.to} key={d.id} data-testid={`ecosystem-division-${i}`} className="block card-cd !p-8 group">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-3 flex items-start gap-4">
                    <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${d.accent} text-white grid place-items-center shadow-lg shadow-blue-500/10 shrink-0`}>
                      <d.icon size={24} strokeWidth={1.6} />
                    </div>
                    <div>
                      <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-slate-400">Division 0{d.id}</div>
                      <h3 className="font-display text-xl font-bold text-[#0A1B5E] mt-1">{d.title}</h3>
                      <div className="text-sm text-slate-500 mt-1">{d.role}</div>
                    </div>
                  </div>
                  <div className="lg:col-span-6 text-slate-600 leading-relaxed">{d.desc}</div>
                  <div className="lg:col-span-3">
                    <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-slate-400 mb-3">Operational Pillars</div>
                    <ul className="space-y-1.5">
                      {d.pillars.map((p) => (
                        <li key={p} className="flex items-center gap-2 text-sm text-[#0A1B5E] font-display font-medium">
                          <Layers size={12} className="text-[#3B82F6]" /> {p}
                        </li>
                      ))}
                    </ul>
                    <span className="mt-5 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">
                      Enter division <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
